import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, setDoc, query, where, deleteDoc } from 'firebase/firestore';
import { FaArrowLeft, FaSave, FaEdit, FaTrash, FaTimes, FaInfoCircle, FaChevronDown, FaChevronUp, FaLock, FaUnlock, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// --- Constants & Global Lists ---
const COUNTRY_LIST = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cambodia", "Cameroon", "Canada", "Chad", "Chile", "China", "Colombia", "Comoros", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia", "Ethiopia", "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Guinea", "Guyana", "Haiti", "Honduras", "Hong Kong", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Liberia", "Libya", "Lithuania", "Luxembourg", "Macau", "Madagascar", "Malaysia", "Maldives", "Mali", "Malta", "Mexico", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Myanmar",
    "Namibia", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "Norway", "Oman",
    "Pakistan", "Palestine", "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
    "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Singapore", "Slovakia", "Slovenia", "Somalia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Uganda", "UK", "Ukraine", "UAE", "USA", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const years = Array.from({length: 15}, (_, i) => (2026 + i).toString()); 
const months = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' }, { value: '03', label: 'March' },
    { value: '04', label: 'April' }, { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' }, { value: '09', label: 'September' },
    { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' }
];
const services = ['DHL', 'FedEx', 'UPS', 'Aramex']; 
const weightsList = Array.from({length: 60}, (_, i) => ((i + 1) * 0.5).toFixed(1));

// --- Sub-component for individual rate input with auto 00.00 formatting ---
const RateInput = ({ initialValue, onSave }) => {
    const formatValue = (val) => {
        if (val === '' || val === undefined || val === null) return '';
        const num = parseFloat(val);
        return isNaN(num) ? '' : num.toFixed(2);
    };

    const [val, setVal] = useState(formatValue(initialValue));

    useEffect(() => {
        setVal(formatValue(initialValue));
    }, [initialValue]);

    const handleBlur = () => {
        if (val === '') {
            setVal(''); 
            onSave('');
        } else {
            const parsed = parseFloat(val);
            if (!isNaN(parsed)) {
                setVal(parsed.toFixed(2));
                onSave(parsed);
            } else {
                setVal(formatValue(initialValue)); 
            }
        }
    };

    return (
        <input
            type="text"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleBlur}
            style={{width: '70px', padding: '6px', textAlign: 'center', border: '1px solid transparent', background: 'transparent', outline: 'none', fontWeight: 'bold', color: '#2980b9'}}
            onFocus={(e) => e.target.style.border = '1px solid #3498db'}
            placeholder=""
        />
    );
};

// --- Custom Modal UI Component ---
const CustomModal = ({ isOpen, type, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(2px)' }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 15px 30px rgba(0,0,0,0.2)', textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 15px', color: type === 'confirm' ? '#e74c3c' : '#2980b9', fontSize: '20px' }}>{title}</h3>
                <p style={{ margin: '0 0 25px', color: '#555', fontSize: '15px', lineHeight: '1.5' }}>{message}</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                    {type === 'confirm' && (
                        <button onClick={onCancel} style={{ padding: '10px 20px', background: '#ecf0f1', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: '0.2s' }}>Cancel</button>
                    )}
                    <button onClick={onConfirm} style={{ padding: '10px 25px', background: type === 'confirm' ? '#e74c3c' : '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: '0.2s' }}>
                        {type === 'confirm' ? 'Confirm' : 'OK'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const BookingRates = () => {
    const navigate = useNavigate();

    // Custom Modal State
    const [modalInfo, setModalInfo] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null, onCancel: null });
    
    const showAlert = (title, message) => {
        setModalInfo({ isOpen: true, type: 'alert', title, message, onConfirm: () => setModalInfo({ isOpen: false }) });
    };

    const showConfirm = (title, message, onConfirmCallback) => {
        setModalInfo({ 
            isOpen: true, 
            type: 'confirm', 
            title, 
            message, 
            onConfirm: () => {
                setModalInfo({ isOpen: false });
                onConfirmCallback();
            },
            onCancel: () => setModalInfo({ isOpen: false }) 
        });
    };

    // Filters 
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(5, 7));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString()); 
    const [selectedService, setSelectedService] = useState('DHL');

    // Bulk Input States
    const [showBulkEntry, setShowBulkEntry] = useState(false);
    const [zoneInput, setZoneInput] = useState(''); // Custom Zone Name
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [countrySearch, setCountrySearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [ratesText, setRatesText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [unlockedZone, setUnlockedZone] = useState(false); // Unlock State
    const dropdownRef = useRef(null);

    // Range States for Bulk Entry
    const [bulkToMonth, setBulkToMonth] = useState('');
    const [bulkToYear, setBulkToYear] = useState('');

    // Data States
    const [rateData, setRateData] = useState({}); 
    const [countryZones, setCountryZones] = useState({}); // Country to Zone Mapping
    const [zoneUpTo, setZoneUpTo] = useState({}); // Zone to UpTo Date Mapping
    const [loadingData, setLoadingData] = useState(false);
    const [inheritedFrom, setInheritedFrom] = useState(''); 
    
    // Matrix Edit State
    const [hasMatrixChanges, setHasMatrixChanges] = useState(false);
    const [isSavingMatrix, setIsSavingMatrix] = useState(false);
    const [expandedZones, setExpandedZones] = useState({}); 

    // Overview Calendar States
    const [overviewAgent, setOverviewAgent] = useState('');
    const [overviewYear, setOverviewYear] = useState(new Date().getFullYear().toString());
    const [overviewData, setOverviewData] = useState({});
    const [loadingOverview, setLoadingOverview] = useState(false);

    // Set "Up To" date to the next month automatically when "From" date changes
    useEffect(() => {
        let nextMonth = parseInt(selectedMonth) + 1;
        let nextYear = parseInt(selectedYear);
        if (nextMonth > 12) {
            nextMonth = 1;
            nextYear++;
        }
        setBulkToMonth(nextMonth.toString().padStart(2, '0'));
        setBulkToYear(nextYear.toString());
    }, [selectedMonth, selectedYear]);

    // Reset unlock state when zone input changes
    useEffect(() => {
        setUnlockedZone(false);
    }, [zoneInput]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const snap = await getDocs(collection(db, "booking_companies"));
                setAgents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) { console.error(err); }
        };
        fetchAgents();
    }, []);

    const getDocId = (monthStr) => {
        if (!selectedAgent) return null;
        return `${selectedAgent}_${monthStr}_${selectedService}`;
    };

    const getMonthsInRange = (startYear, startMonth, endYear, endMonth) => {
        const result = [];
        let currY = parseInt(startYear);
        let currM = parseInt(startMonth);
        const endY = parseInt(endYear);
        const endM = parseInt(endMonth);

        if (currY > endY || (currY === endY && currM > endM)) {
            return [];
        }

        while (currY < endY || (currY === endY && currM <= endM)) {
            const monthStr = currM.toString().padStart(2, '0');
            result.push(`${currY}-${monthStr}`);
            currM++;
            if (currM > 12) {
                currM = 1;
                currY++;
            }
        }
        return result;
    };

    const fetchRates = useCallback(async () => {
        if (!selectedAgent || !selectedYear) { 
            setRateData({}); setCountryZones({}); setZoneUpTo({}); setInheritedFrom(''); return; 
        }
        
        setLoadingData(true);
        setExpandedZones({}); 
        try {
            const currentMonthStr = `${selectedYear}-${selectedMonth}`;
            
            const q = query(
                collection(db, 'booking_rates'),
                where('agentId', '==', selectedAgent),
                where('service', '==', selectedService)
            );
            
            const querySnapshot = await getDocs(q);
            const allDocs = querySnapshot.docs.map(d => d.data());

            let rawRateData = {};
            let rawCountryZones = {};
            let rawZoneUpTo = {};
            let newInheritedFrom = '';

            const exactMatch = allDocs.find(d => d.month === currentMonthStr);
            
            if (exactMatch && exactMatch.countryRates) {
                rawRateData = exactMatch.countryRates;
                rawCountryZones = exactMatch.countryZones || {};
                rawZoneUpTo = exactMatch.zoneUpTo || {};
            } else {
                const pastDocs = allDocs.filter(d => d.month < currentMonthStr);
                if (pastDocs.length > 0) {
                    pastDocs.sort((a, b) => b.month.localeCompare(a.month));
                    rawRateData = pastDocs[0].countryRates || {};
                    rawCountryZones = pastDocs[0].countryZones || {};
                    rawZoneUpTo = pastDocs[0].zoneUpTo || {};
                    newInheritedFrom = pastDocs[0].month;
                }
            }

            // --- EKHAANE VALIDITY CHECK KORA HOCCHE ---
            // Jekono zone er Up To date jodi currentMonthStr er theke choto hoy, 
            // tahole seta matrix a asbe na.
            const activeRateData = {};
            const activeCountryZones = {};
            const activeZoneUpTo = {};

            Object.keys(rawCountryZones).forEach(country => {
                const zoneName = rawCountryZones[country];
                const upTo = rawZoneUpTo[zoneName];

                // Jodi kono Up To date na thake ba Up To date current month er soman ba boro hoy (meaning expired na)
                if (!upTo || upTo >= currentMonthStr) {
                    activeRateData[country] = rawRateData[country];
                    activeCountryZones[country] = zoneName;
                    if (upTo) activeZoneUpTo[zoneName] = upTo;
                }
            });

            setRateData(activeRateData);
            setCountryZones(activeCountryZones);
            setZoneUpTo(activeZoneUpTo);
            setInheritedFrom(newInheritedFrom);

            setHasMatrixChanges(false); 
        } catch (error) { console.error("Error fetching rates:", error); }
        setLoadingData(false);
    }, [selectedAgent, selectedMonth, selectedYear, selectedService]);

    useEffect(() => {
        fetchRates();
    }, [fetchRates]);

    // Fetch Overview Data
    const fetchOverviewData = useCallback(async () => {
        if (!overviewAgent || !overviewYear) {
            setOverviewData({}); return;
        }
        setLoadingOverview(true);
        try {
            const q = query(
                collection(db, 'booking_rates'),
                where('agentId', '==', overviewAgent)
            );
            const snap = await getDocs(q);
            
            const data = {}; 
            snap.docs.forEach(docSnap => {
                const d = docSnap.data();
                if (d.month && d.month.startsWith(overviewYear)) {
                    const monthNum = d.month.split('-')[1];
                    if (!data[monthNum]) data[monthNum] = {};
                    
                    const zones = Object.values(d.countryZones || {});
                    const uniqueZones = [...new Set(zones)];
                    
                    if (uniqueZones.length > 0) {
                        data[monthNum][d.service] = uniqueZones;
                    }
                }
            });
            setOverviewData(data);
        } catch (err) {
            console.error("Overview Fetch Error:", err);
        }
        setLoadingOverview(false);
    }, [overviewAgent, overviewYear]);

    useEffect(() => {
        fetchOverviewData();
    }, [fetchOverviewData]);

    const formatInheritedMonth = (monthStr) => {
        if (!monthStr) return '';
        const [y, m] = monthStr.split('-');
        const monthName = months.find(mo => mo.value === m)?.label || m;
        return `${monthName} ${y}`;
    };

    const handleAddCountry = (country) => {
        if (!selectedCountries.includes(country)) {
            setSelectedCountries([...selectedCountries, country]);
        }
        setCountrySearch('');
        setShowDropdown(false);
    };

    const handleRemoveCountry = (countryToRemove) => {
        setSelectedCountries(selectedCountries.filter(c => c !== countryToRemove));
    };

    // Determine Lock Status for "Up To"
    const currentZoneClean = zoneInput.trim();
    const existingUpTo = zoneUpTo[currentZoneClean];
    const isLocked = !!existingUpTo && !unlockedZone;

    // Filter Countries Logic (Allows expired countries to show up again)
    const filteredCountries = COUNTRY_LIST.filter(c => {
        if (!c.toLowerCase().includes(countrySearch.toLowerCase())) return false;
        if (selectedCountries.includes(c)) return false;
        
        const existingZone = countryZones[c];
        if (existingZone && existingZone !== currentZoneClean) {
            // Ekhon ar extra check dorkar nai karon fetchRates er vetorei expired data bad dewa hoyeche.
            // Jodi existingZone thake tarmane sheta valid state e ache.
            return false; 
        }
        return true;
    });
    
    let displayToMonth = bulkToMonth;
    let displayToYear = bulkToYear;

    if (isLocked) {
        const [y, m] = existingUpTo.split('-');
        displayToMonth = m;
        displayToYear = y;
    }

    const handleBulkSave = async () => {
        if (!selectedAgent) return showAlert("Validation Error", "Please select a Booking Agent first.");
        if (!zoneInput.trim()) return showAlert("Validation Error", "Please enter a Zone Name.");
        if (selectedCountries.length === 0 || !ratesText.trim()) return showAlert("Validation Error", "Countries and Rates cannot be empty.");

        const targetMonth = isLocked ? displayToMonth : bulkToMonth;
        const targetYear = isLocked ? displayToYear : bulkToYear;

        const monthsToSave = getMonthsInRange(selectedYear, selectedMonth, targetYear, targetMonth);
        if (monthsToSave.length === 0) return showAlert("Invalid Date Range", "The 'Up To' Date must be after or equal to the 'From' Date.");

        const ratesList = ratesText.split('\n').map(r => {
            const val = r.trim();
            if (val === '') return '';
            const num = parseFloat(val);
            return isNaN(num) ? '' : num;
        });

        while(ratesList.length > 0 && ratesList[ratesList.length - 1] === '') {
            ratesList.pop();
        }

        if (ratesList.length === 0) return showAlert("Invalid Data", "Valid rate data not found.");

        setIsSaving(true);
        try {
            const newCountryRates = { ...rateData };
            const newCountryZones = { ...countryZones };
            const newZoneUpTo = { ...zoneUpTo };
            
            selectedCountries.forEach(country => {
                newCountryRates[country] = [...ratesList]; 
                newCountryZones[country] = zoneInput.trim();
            });

            newZoneUpTo[zoneInput.trim()] = `${targetYear}-${targetMonth}`;

            const agentName = agents.find(a => a.id === selectedAgent)?.name || '';

            const promises = monthsToSave.map(monthStr => {
                const docRef = doc(db, 'booking_rates', getDocId(monthStr));
                return setDoc(docRef, {
                    agentId: selectedAgent,
                    agentName: agentName,
                    month: monthStr,
                    service: selectedService,
                    countryRates: newCountryRates,
                    countryZones: newCountryZones,
                    zoneUpTo: newZoneUpTo
                }, { merge: true }); 
            });

            await Promise.all(promises);

            showAlert('Success', `Zone "${zoneInput.trim()}" has been saved successfully up to ${targetMonth}/${targetYear}!`);
            setSelectedCountries([]);
            setRatesText('');
            setZoneInput('');
            setInheritedFrom('');
            setUnlockedZone(false);
            fetchRates(); 
        } catch (error) {
            console.error("Save Error:", error);
            showAlert("Error", "There was an error saving the rates. Please try again.");
        }
        setIsSaving(false);
    };

    // Delete All Rates Feature with Custom Modal
    const handleDeleteAllServiceRates = async () => {
        if (!selectedAgent || !selectedService) return;
        const agentName = agents.find(a => a.id === selectedAgent)?.name || 'Unknown Agent';
        
        showConfirm(
            'Delete All Rates?',
            `Are you sure you want to delete ALL ${selectedService} rates for ${agentName}? This action cannot be undone!`,
            async () => {
                setIsSaving(true);
                try {
                    const q = query(
                        collection(db, 'booking_rates'),
                        where('agentId', '==', selectedAgent),
                        where('service', '==', selectedService)
                    );
                    const snapshot = await getDocs(q);
                    const deletePromises = snapshot.docs.map(d => deleteDoc(doc(db, 'booking_rates', d.id)));
                    await Promise.all(deletePromises);
                    
                    setRateData({});
                    setCountryZones({});
                    setZoneUpTo({});
                    setInheritedFrom('');
                    
                    showAlert('Success', 'All rates have been successfully deleted!');
                    fetchRates();
                } catch (error) {
                    console.error("Delete All Error:", error);
                    showAlert("Error", "There was a problem deleting the rates.");
                }
                setIsSaving(false);
            }
        );
    };

    const getGroups = () => {
        const groupsMap = {};
        Object.entries(rateData).forEach(([country, rates]) => {
            const zName = countryZones[country] || 'Unassigned Zone';
            if (!groupsMap[zName]) {
                groupsMap[zName] = { 
                    zoneName: zName, 
                    countries: [], 
                    rates: rates, 
                    upTo: zoneUpTo[zName] || '' 
                };
            }
            groupsMap[zName].countries.push(country);
        });

        const groupsArray = Object.values(groupsMap);
        groupsArray.sort((a, b) => a.zoneName.localeCompare(b.zoneName));
        return groupsArray;
    };

    const currentGroups = getGroups();

    const toggleZone = (idx) => {
        setExpandedZones(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleGroupRateEdit = (countriesInGroup, weightIndex, newValue) => {
        const parsedValue = newValue === '' ? '' : (parseFloat(newValue) || 0);
        const updatedRates = { ...rateData };
        
        countriesInGroup.forEach(country => {
            const countryArray = [...(updatedRates[country] || [])]; 
            while (countryArray.length <= weightIndex) { countryArray.push(''); }
            countryArray[weightIndex] = parsedValue;
            updatedRates[country] = countryArray;
        });

        setRateData(updatedRates);
        setHasMatrixChanges(true); 
    };

    const saveMatrixChanges = async () => {
        setIsSavingMatrix(true);
        try {
            const agentName = agents.find(a => a.id === selectedAgent)?.name || '';
            const monthStr = `${selectedYear}-${selectedMonth}`;
            
            const docRef = doc(db, 'booking_rates', getDocId(monthStr));
            await setDoc(docRef, {
                agentId: selectedAgent,
                agentName: agentName,
                month: monthStr,
                service: selectedService,
                countryRates: rateData,
                countryZones: countryZones,
                zoneUpTo: zoneUpTo
            }, { merge: true });

            setHasMatrixChanges(false);
            setInheritedFrom(''); 
            showAlert('Success', 'Matrix changes saved for the current month!');
        } catch (error) {
            console.error("Matrix save error:", error);
            showAlert('Error', 'Failed to save changes.');
        }
        setIsSavingMatrix(false);
    };

    const handleDeleteGroup = async (group) => {
        showConfirm(
            'Delete Zone Rates?',
            `Are you sure you want to delete all rates for ${group.zoneName}?`,
            async () => {
                const updatedRates = { ...rateData };
                const updatedZones = { ...countryZones };
                
                group.countries.forEach(c => {
                    delete updatedRates[c];
                    delete updatedZones[c];
                });
                
                setRateData(updatedRates);
                setCountryZones(updatedZones);
                
                try {
                    const monthStr = `${selectedYear}-${selectedMonth}`;
                    const docRef = doc(db, 'booking_rates', getDocId(monthStr));

                    await setDoc(docRef, {
                        countryRates: updatedRates,
                        countryZones: updatedZones
                    }, { merge: true });

                    setInheritedFrom('');
                    setHasMatrixChanges(false);
                } catch (error) {
                    console.error("Matrix delete error:", error);
                    showAlert('Error', 'Failed to delete from database.');
                }
            }
        );
    };

    const loadGroupForEdit = (group) => {
        setSelectedCountries(group.countries);
        setZoneInput(group.zoneName !== 'Unassigned Zone' ? group.zoneName : '');
        setShowBulkEntry(true); 
        
        let rates = group.rates;
        let lastValidIndex = rates.length - 1;
        while(lastValidIndex >= 0 && (rates[lastValidIndex] === 0 || rates[lastValidIndex] === '' || rates[lastValidIndex] === undefined || rates[lastValidIndex] === null)) {
            lastValidIndex--;
        }
        
        const displayRates = rates.slice(0, lastValidIndex + 1);
        setRatesText(displayRates.map(r => (r === '' || r === undefined || r === null) ? '' : Number(r).toFixed(2)).join('\n'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const container = { padding: '20px', background: '#f4f6f8', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" };
    const card = { background: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: '20px' };
    const inputStyle = { padding: '10px', border: '1px solid #ddd', borderRadius: '5px', outline: 'none', width: '100%', fontSize: '14px' };
    const textAreaStyle = { width: '100%', height: '220px', padding: '10px', border: '1px solid #3498db', borderRadius: '5px', fontSize: '14px', resize: 'vertical', lineHeight: '1.5' };
    const th = { background: '#2c3e50', padding: '10px', color: 'white', border: '1px solid #34495e', textAlign: 'center', fontSize: '13px' };
    const td = { padding: '5px', border: '1px solid #ddd', textAlign: 'center' };

    return (
        <div style={container}>
            <CustomModal {...modalInfo} />
            
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
                <div>
                    <button onClick={() => navigate('/dashboard/booking-report')} style={{display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: '#3498db', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', padding: 0, marginBottom: '10px'}}>
                        <FaArrowLeft /> Back to Booking Report
                    </button>
                    <h2 style={{color: '#2c3e50', margin: 0}}>Booking Rate Setup</h2>
                </div>
                {selectedAgent && (
                    <div style={{display: 'flex', alignItems: 'flex-end'}}>
                        <button onClick={handleDeleteAllServiceRates} disabled={isSaving} style={{background: '#e74c3c', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', transition: '0.3s'}}>
                            <FaTrash /> Delete All {selectedService} Rates
                        </button>
                    </div>
                )}
            </div>

            <div style={{...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', background: '#ecf0f1'}}>
                <div>
                    <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>Booking Agent <span style={{color:'red'}}>*</span></label>
                    <select value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)} style={inputStyle}>
                        <option value="">Select Agent...</option>
                        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>Service Type</label>
                    <select value={selectedService} onChange={e => setSelectedService(e.target.value)} style={inputStyle}>
                        {services.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>From Month</label>
                    <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={inputStyle}>
                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>From Year</label>
                    <input 
                        type="number" 
                        value={selectedYear} 
                        onChange={e => setSelectedYear(e.target.value)} 
                        style={inputStyle} 
                        placeholder="e.g. 2026"
                    />
                </div>
            </div>

            {selectedAgent && (
                <div style={{...card, borderTop: '5px solid #3498db'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBulkEntry ? '15px' : '0'}}>
                        <h3 style={{margin: '0', color: '#2980b9'}}>Bulk Rate Entry</h3>
                        <button 
                            onClick={() => setShowBulkEntry(!showBulkEntry)} 
                            style={{background: 'none', border: '1px solid #3498db', color: '#3498db', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '12px'}}
                        >
                            {showBulkEntry ? <FaChevronUp /> : <FaChevronDown />}
                            {showBulkEntry ? 'Hide Bulk Entry' : 'Show Bulk Entry'}
                        </button>
                    </div>
                    
                    {showBulkEntry && (
                        <>
                            <div style={{background: '#f0f8ff', padding: '15px', borderRadius: '5px', marginBottom: '15px', border: '1px dashed #3498db', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', justifyContent: 'space-between'}}>
                                <div>
                                    <p style={{margin: '0 0 5px', fontSize: '13px', color: '#333', fontWeight: 'bold'}}>Apply These Rates From:</p>
                                    <p style={{margin: 0, fontSize: '14px', color: '#2980b9'}}>
                                        {months.find(m=>m.value===selectedMonth)?.label} {selectedYear}
                                    </p>
                                </div>
                                <div style={{flex: 1, minWidth: '200px', maxWidth: '300px', marginLeft: '20px'}}>
                                    <label style={{fontWeight: 'bold', fontSize: '12px', color: '#333'}}>Zone Name <span style={{color:'red'}}>*</span></label>
                                    <input 
                                        type="text" 
                                        value={zoneInput} 
                                        onChange={(e) => setZoneInput(e.target.value)} 
                                        placeholder="e.g. Zone 1, Zone EU..." 
                                        style={{...inputStyle, padding: '8px', border: '2px solid #3498db'}} 
                                    />
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px', background: isLocked ? '#ffeaa7' : 'transparent', padding: '5px', borderRadius: '5px'}}>
                                    <p style={{margin: 0, fontSize: '13px', color: '#333', fontWeight: 'bold'}}>Up To:</p>
                                    <select value={displayToMonth} disabled={isLocked} onChange={e => setBulkToMonth(e.target.value)} style={{...inputStyle, padding: '8px', width: '120px', background: isLocked ? '#f1f2f6' : 'white'}}>
                                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                    <select value={displayToYear} disabled={isLocked} onChange={e => setBulkToYear(e.target.value)} style={{...inputStyle, padding: '8px', width: '100px', background: isLocked ? '#f1f2f6' : 'white'}}>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    
                                    {!!existingUpTo && !unlockedZone && (
                                        <span 
                                            onClick={() => {
                                                showConfirm(
                                                    'Unlock Zone Duration?',
                                                    'Are you sure you want to unlock the "Up To" date for this zone? Saving after unlocking will update the date range in the database.',
                                                    () => setUnlockedZone(true)
                                                );
                                            }} 
                                            style={{color: '#d35400', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', padding: '4px 8px', border: '1px solid #d35400', borderRadius: '4px', background: 'white'}} 
                                            title="Click to Unlock"
                                        >
                                            <FaLock /> Locked (Unlock)
                                        </span>
                                    )}
                                    {unlockedZone && (
                                        <span style={{color: '#27ae60', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 'bold', padding: '4px 8px', border: '1px solid #27ae60', borderRadius: '4px', background: 'white'}} title="Unlocked">
                                            <FaUnlock /> Unlocked
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                                <div style={{flex: 1, minWidth: '300px'}} ref={dropdownRef}>
                                    <label style={{fontWeight: 'bold', color: '#333', display: 'block', marginBottom: '5px'}}>Countries (Filtered)</label>
                                    
                                    <div style={{border: '1px solid #3498db', borderRadius: '5px', padding: '10px', minHeight: '220px', background: '#fdfdfd', display: 'flex', flexDirection: 'column'}}>
                                        
                                        <div style={{position: 'relative', marginBottom: '10px'}}>
                                            <input 
                                                type="text" 
                                                placeholder="Type & Click to select countries..." 
                                                value={countrySearch}
                                                onChange={(e) => {
                                                    setCountrySearch(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => setShowDropdown(true)}
                                                onClick={() => setShowDropdown(true)}
                                                style={{width: '100%', border: '1px solid #bdc3c7', borderRadius: '4px', padding: '8px 12px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: '0.3s', background: 'white'}}
                                            />
                                            
                                            {showDropdown && (
                                                <div style={{position: 'absolute', top: 'calc(100% + 5px)', left: 0, width: '100%', maxHeight: '180px', overflowY: 'auto', background: 'white', border: '1px solid #ddd', borderRadius: '5px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', zIndex: 1000}}>
                                                    {filteredCountries.length > 0 ? filteredCountries.map(c => (
                                                        <div 
                                                            key={c} 
                                                            onClick={() => handleAddCountry(c)}
                                                            style={{padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '13px', color: '#333'}}
                                                            onMouseEnter={(e) => { e.target.style.background = '#eaf2f8'; e.target.style.color = '#2980b9'; }}
                                                            onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.color = '#333'; }}
                                                        >
                                                            {c}
                                                        </div>
                                                    )) : (
                                                        <div style={{padding: '10px 12px', color: '#e74c3c', fontSize: '13px', textAlign: 'center'}}>No matches found / Country currently locked</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div style={{flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '8px', alignContent: 'flex-start', padding: '5px 0'}}>
                                            {selectedCountries.length > 0 ? selectedCountries.map(country => (
                                                <span key={country} style={{background: '#3498db', color: 'white', padding: '5px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 'bold', height: 'fit-content'}}>
                                                    {country}
                                                    <FaTimes style={{cursor: 'pointer'}} onClick={() => handleRemoveCountry(country)} title="Remove" />
                                                </span>
                                            )) : (
                                                <div style={{color: '#95a5a6', fontSize: '13px', width: '100%', textAlign: 'center', marginTop: '20px', fontStyle: 'italic'}}>
                                                    No countries selected yet.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{flex: 1, minWidth: '300px'}}>
                                    <label style={{fontWeight: 'bold', color: '#333', display: 'block', marginBottom: '5px'}}>Rates (One per line)</label>
                                    <textarea 
                                        placeholder="00.00&#10;00.00&#10;00.00" 
                                        value={ratesText} 
                                        onChange={e => setRatesText(e.target.value)} 
                                        style={{...textAreaStyle, border: '1px solid #27ae60', background: '#f9fdfa'}} 
                                    />
                                </div>
                            </div>
                            
                            <div style={{textAlign: 'right', marginTop: '15px'}}>
                                <button onClick={handleBulkSave} disabled={isSaving} style={{background: '#27ae60', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', fontSize: '15px'}}>
                                    <FaSave /> {isSaving ? 'Saving...' : 'Save Bulk Rates'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {selectedAgent && !loadingData && currentGroups.length > 0 && (
                <div style={{...card, padding: 0, overflowX: 'auto'}}>
                    
                    <div style={{padding: '15px', background: '#34495e', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
                        <div>
                            <h4 style={{margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                Weight-Wise Rates Matrix
                                {inheritedFrom && (
                                    <span style={{background: '#f39c12', color: 'white', padding: '3px 8px', borderRadius: '15px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                        <FaInfoCircle /> Carried over from {formatInheritedMonth(inheritedFrom)}
                                    </span>
                                )}
                            </h4>
                            <p style={{margin: '5px 0 0', fontSize: '12px', color: '#bdc3c7'}}>Rates applied to grouped countries. Edit rates directly below.</p>
                        </div>
                        
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end'}}>
                            {hasMatrixChanges && (
                                <button 
                                    onClick={saveMatrixChanges} 
                                    disabled={isSavingMatrix}
                                    style={{background: '#e67e22', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', transition: '0.3s'}}
                                >
                                    <FaSave /> {isSavingMatrix ? 'Saving...' : 'Save Matrix Changes'}
                                </button>
                            )}
                        </div>
                    </div>

                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                            <tr>
                                <th style={{...th, position: 'sticky', left: 0, top: 0, zIndex: 3, background: '#2c3e50', width: '80px'}}>Weight</th>
                                {currentGroups.map((group, groupIdx) => (
                                    <th key={groupIdx} style={{...th, minWidth: '150px', position: 'sticky', top: 0, zIndex: 2}}>
                                        <div style={{marginBottom: '10px'}}>
                                            <div 
                                                onClick={() => toggleZone(groupIdx)} 
                                                style={{cursor: 'pointer', background: '#2980b9', padding: '6px', borderRadius: '4px', fontWeight: 'bold', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '2px', fontSize: '13px', color: 'white', transition: '0.3s'}}
                                                title="Click to view/hide countries"
                                                onMouseEnter={(e) => e.target.style.background = '#3498db'}
                                                onMouseLeave={(e) => e.target.style.background = '#2980b9'}
                                            >
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {group.zoneName} 
                                                    <span style={{fontSize: '11px', background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '10px'}}>
                                                        {group.countries.length}
                                                    </span>
                                                </div>
                                                
                                                {/* SHOW VALID UP TO IN MATRIX */}
                                                {group.upTo && (
                                                    <div style={{fontSize: '10px', color: '#f1c40f', fontWeight: 'normal', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px'}}>
                                                        <FaLock size={8}/> Valid Up To: {formatInheritedMonth(group.upTo)}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {expandedZones[groupIdx] && (
                                                <div style={{marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '150px', overflowY: 'auto', padding: '8px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px', textAlign: 'left'}}>
                                                    {group.countries.map(c => (
                                                        <span key={c} style={{fontSize: '12px', color: '#ecf0f1', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '3px'}}>
                                                            • {c}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div style={{display: 'flex', gap: '5px', justifyContent: 'center'}}>
                                            <button onClick={() => loadGroupForEdit(group)} title="Edit Group in Bulk" style={{background: '#f39c12', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                <FaEdit /> Edit
                                            </button>
                                            <button onClick={() => handleDeleteGroup(group)} title="Delete Group" style={{background: '#e74c3c', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                <FaTrash /> Del
                                            </button>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {weightsList.map((weightStr, rowIndex) => (
                                <tr key={rowIndex} style={{background: rowIndex % 2 === 0 ? 'white' : '#f8f9fa'}}>
                                    <td style={{...td, fontWeight: 'bold', background: '#ecf0f1', position: 'sticky', left: 0, zIndex: 1, borderRight: '2px solid #bdc3c7', color: '#333'}}>
                                        {weightStr} KG
                                    </td>
                                    {currentGroups.map((group, groupIdx) => {
                                        const val = group.rates[rowIndex] !== undefined ? group.rates[rowIndex] : '';
                                        return (
                                            <td key={groupIdx} style={td}>
                                                <RateInput 
                                                    initialValue={val} 
                                                    onSave={(newValue) => handleGroupRateEdit(group.countries, rowIndex, newValue)} 
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedAgent && !loadingData && currentGroups.length === 0 && (
                <div style={{textAlign: 'center', padding: '40px', color: '#95a5a6'}}>
                    <p style={{fontSize: '18px', fontWeight: 'bold'}}>No rates configured yet.</p>
                    <p>Use the Bulk Rate Entry form above to add rates for {months.find(m=>m.value===selectedMonth)?.label} {selectedYear}.</p>
                </div>
            )}

            {/* Overview Calendar Section Added Here */}
            <div style={{...card, marginTop: '30px', borderTop: '5px solid #8e44ad'}}>
                <h3 style={{margin: '0 0 15px', color: '#8e44ad', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <FaCalendarAlt /> Rate Setup Overview (Calendar View)
                </h3>
                
                <div style={{display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap'}}>
                    <div style={{minWidth: '200px'}}>
                        <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>Select Company for Overview</label>
                        <select value={overviewAgent} onChange={e => setOverviewAgent(e.target.value)} style={inputStyle}>
                            <option value="">Select Company...</option>
                            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                    </div>
                    <div style={{minWidth: '150px'}}>
                        <label style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>Select Year</label>
                        <select value={overviewYear} onChange={e => setOverviewYear(e.target.value)} style={inputStyle}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {overviewAgent ? (
                    loadingOverview ? (
                        <p style={{color: '#7f8c8d'}}>Loading overview details...</p>
                    ) : (
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px'}}>
                            {months.map(m => {
                                const monthData = overviewData[m.value];
                                return (
                                    <div key={m.value} style={{border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                                        <div style={{background: '#f1f2f6', padding: '10px', fontWeight: 'bold', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#2c3e50'}}>
                                            {m.label} {overviewYear}
                                        </div>
                                        <div style={{padding: '10px', minHeight: '80px', background: 'white', fontSize: '13px'}}>
                                            {!monthData || Object.keys(monthData).length === 0 ? (
                                                <div style={{color: '#95a5a6', textAlign: 'center', fontStyle: 'italic', marginTop: '10px'}}>No rates set</div>
                                            ) : (
                                                Object.keys(monthData).map(svc => (
                                                    <div key={svc} style={{marginBottom: '8px', borderBottom: '1px dashed #eee', paddingBottom: '5px'}}>
                                                        <span style={{fontWeight: 'bold', color: '#2980b9'}}>{svc}: </span>
                                                        <span style={{color: '#555'}}>{monthData[svc].join(', ')}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                ) : (
                    <p style={{color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '5px'}}>
                        Please select a company to view the overview calendar.
                    </p>
                )}
            </div>
        </div>
    );
};

export default BookingRates;